import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/app/DataTable";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { StudioBulkBar } from "@/components/items/StudioBulkBar";

// Approve / reject straight from the Approvals list; each type calls its own decision endpoint
const CALLS = {
  lm: (r, ok, c) => api.post(`/approvals/${r.id}/decision`, { decision: ok ? "approved" : "rejected", comment: c }),
  plan_change: (r, ok, c) => api.post(`/approvals/plan-changes/${r.id}/decision`, { decision: ok ? "approved" : "rejected", comment: c }),
  budget_change: (r, ok, c) => api.post(`/orders/${r.order_id}/budget-change/decide`, { approve: ok, comment: c }),
  billing: (r, ok, c) => api.post(`/billing-lines/${r.id}/approve`, { decision: ok ? "Approved" : "Rejected", remarks: c }),
  qc: (r, ok, c) => api.post(`/qc-submissions/${r.id}/decision`, { qc_status: ok ? "approved" : "rejected", qc_remarks: c }),
  design: (r, ok, c) => (ok ? api.post(`/designs/${r.design_id}/revisions/${r.id}/approve`) : api.post(`/designs/${r.design_id}/revisions/${r.id}/reject`, { comment: c })),
  item: (r, ok, c) => api.post(`/items/${encodeURIComponent(r.id)}/decision`, { decision: ok ? "approved" : "rejected", comment: c }),
};
const OPEN_ONLY = ["lm_short_close"]; // decided on the LM with a live over-invoicing check (Admin only)
const PERM = { lm: "orders.approve", plan_change: "orders.approve", budget_change: "orders.approve", billing: "orders.approve", qc: "qc.approve", design: "design.approve", item: "parts.approve" };
const NO_SELF = ["lm", "plan_change", "budget_change", "billing", "design", "item"];

function Decision({ r, onDone }) {
  const { can, me } = useMe();
  const [busy, setBusy] = useState(false);
  if (OPEN_ONLY.includes(r.kind) || !can(PERM[r.kind])) return <Button size="sm" variant="outline" className="h-7" asChild><Link to={r.link} data-testid={`all-approvals-open-${r.kind}-${r.id}`}>Open →</Link></Button>;
  if (NO_SELF.includes(r.kind) && r.requested_by?.user_id === me.user_id && !me.roles.includes("admin")) return <span className="text-[11px] text-slate-500">Awaiting another approver</span>;
  const decide = async (ok) => {
    const c = ok ? "" : window.prompt(`Reason for rejecting ${r.ref}?`);
    if (!ok && !c) return;
    setBusy(true);
    try { await CALLS[r.kind](r, ok, c); toast.success(`${r.kind_label} ${r.ref}: ${ok ? "approved" : "rejected"}`); onDone(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const tid = `${r.kind}-${r.id}`;
  return (
    <span className="flex gap-1">
      <Button size="sm" className="h-7" disabled={busy} onClick={() => decide(true)} data-testid={`all-approvals-approve-${tid}`}>Approve</Button>
      <Button size="sm" variant="outline" className="h-7 text-red-600" disabled={busy} onClick={() => decide(false)} data-testid={`all-approvals-reject-${tid}`}>Reject</Button>
    </span>
  );
}

// Everything waiting for a decision across the app, with a link to where it is decided
export function AllApprovals({ onChanged }) {
  const { can } = useMe();
  const [kind, setKind] = useState("all");
  const [picked, setPicked] = useState(new Set());
  const { data, reload } = useFetch("/approvals/all");
  const done = () => { setPicked(new Set()); reload(); onChanged?.(); };
  const rows = (data?.rows || []).filter((r) => kind === "all" || r.kind === kind);
  const studio = useMemo(() => (data?.rows || []).filter((r) => r.kind === "item" && r.from_studio), [data]);
  const studioPicked = studio.filter((r) => picked.has(`item-${r.id}`)).map((r) => r.id);
  const bulk = can("parts.approve") && studio.length > 0 && (kind === "item" || kind === "all");
  const cols = [
    { key: "kind_label", label: "Type", render: (r) => <span>{r.kind_label}{r.from_studio && <span className="block text-[11px] font-semibold text-indigo-700" data-testid={`all-approvals-studio-${r.id}`}>from Engineering Studio</span>}{r.over_invoiced && <span className="ml-1 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold text-white" data-testid={`all-approvals-over-invoiced-${r.id}`}>Over-invoiced</span>}</span> },
    { key: "ref", label: "Reference", render: (r) => <Link to={r.link} className="font-mono font-semibold text-sky-700 hover:underline" data-testid={`all-approvals-open-${r.kind}-${r.id}`}>{r.ref}</Link> },
    { key: "detail", label: "What changed / waiting", wrap: true },
    { key: "requested_by", label: "Requested by", value: (r) => r.requested_by?.name, render: (r) => <span>{r.requested_by?.name || "—"}<span className="block text-[11px] text-slate-500">{fmtDateTime(r.requested_at)}</span></span> },
    { key: "act", label: "", exp: false, render: (r) => <Decision r={r} onDone={done} /> },
  ];
  const counts = data?.counts || {};
  const total = Object.values(counts).reduce((s, n) => s + n, 0);
  return (
    <section className="mb-8" data-testid="all-approvals">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold">Waiting for a decision{total ? <span className="ml-2 rounded-full bg-amber-500 px-2 py-0.5 text-xs text-white" data-testid="all-approvals-count">{total}</span> : null}</h2>
        {[["all", "All", total], ...Object.entries(data?.labels || {}).map(([k, l]) => [k, l, counts[k] || 0])].map(([k, l, n]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`all-approvals-filter-${k}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${kind === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>{l} ({n})</button>))}
      </div>
      {bulk && <p className="mb-2 text-xs text-slate-500" data-testid="all-approvals-studio-hint">Tick Engineering Studio drafts to approve or reject them together.</p>}
      <DataTable testid="all-approvals-table" columns={cols} rows={rows} rowId={(r) => `${r.kind}-${r.id}`} exportName="Approvals_waiting" exportPerm="orders.export" empty="Nothing waiting for a decision"
        {...(bulk ? { selected: picked, onSelect: setPicked } : {})}
        toolbar={bulk && picked.size > 0 && <StudioBulkBar codes={studioPicked} ignored={picked.size - studioPicked.length} onDone={done} />} />
    </section>
  );
}
